import { motion } from "framer-motion";

interface VolumeToggleProps {
  isMuted: boolean;
  onClick: () => void;
}

const VolumeToggle = ({ isMuted, onClick }: VolumeToggleProps) => {
  return (
    <motion.button
      onClick={onClick}
      className="relative w-24 h-24 flex items-center justify-center group"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label={isMuted ? "Unmute video" : "Mute video"}
    >
      {/* Wavy spinning outer ring */}
      <motion.svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        animate={{ rotate: 360 }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <path
          d="M50 5
             C60 5, 65 15, 70 20
             C80 30, 95 35, 95 50
             C95 65, 80 70, 70 80
             C65 85, 60 95, 50 95
             C40 95, 35 85, 30 80
             C20 70, 5 65, 5 50
             C5 35, 20 30, 30 20
             C35 15, 40 5, 50 5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          className="text-white/25 group-hover:text-white/50 transition-colors"
        />
      </motion.svg>

      {/* Second wavy ring - counter rotating */}
      <motion.svg
        className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)]"
        viewBox="0 0 100 100"
        animate={{ rotate: -360 }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <path
          d="M50 8
             C58 12, 68 8, 75 18
             C85 25, 92 38, 92 50
             C92 62, 85 75, 75 82
             C68 92, 58 88, 50 92
             C42 88, 32 92, 25 82
             C15 75, 8 62, 8 50
             C8 38, 15 25, 25 18
             C32 8, 42 12, 50 8Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.75"
          className="text-white/20 group-hover:text-white/40 transition-colors"
        />
      </motion.svg>

      {/* Volume icon */}
      <svg
        viewBox="0 0 24 24"
        className="w-8 h-8 text-white/40 group-hover:text-white/70 transition-colors relative z-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Speaker base */}
        <path d="M11 5L6 9H2v6h4l5 4V5z" />
        
        {/* Sound waves - show when not muted */}
        {!isMuted && (
          <>
            <motion.path
              d="M15.54 8.46a5 5 0 0 1 0 7.07"
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: 1, pathLength: 1 }}
              transition={{ duration: 0.3 }}
            />
            <motion.path
              d="M19.07 4.93a10 10 0 0 1 0 14.14"
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: 1, pathLength: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            />
          </>
        )}
        
        {/* X mark - show when muted */}
        {isMuted && (
          <motion.path
            d="M23 9l-6 6m0-6l6 6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </svg>
    </motion.button>
  );
};

export default VolumeToggle;
